import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Info } from "lucide-react";

export default function AutomationSettingsTab() {
  const [settings, setSettings] = useState({
    engine_enabled: false,
    ai_enabled: false,
    min_similarity_threshold: 0.85,
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.adminAutoGetSettings().then((s) => setSettings((prev) => ({ ...prev, ...s }))).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.adminAutoPutSettings(settings);
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="max-w-2xl space-y-6">
      <div>
        <h2 className="font-serif text-lg font-bold text-blueblack">Configuración</h2>
        <p className="text-xs text-textgray">Preferencias generales del sistema de automatización.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 text-sm flex gap-2">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          Los interruptores están disponibles pero el motor de scraping y las funciones de IA que dependen de él
          aún no están implementados. Podés dejarlos apagados; se activarán en la próxima fase.
        </div>
      </div>

      <div className="bg-white border border-softblue p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-nightblue">Motor de importación</div>
            <div className="text-xs text-textgray">Cuando esté activo, ejecutará cada fuente según su frecuencia.</div>
          </div>
          <Switch
            checked={settings.engine_enabled}
            onCheckedChange={(v) => setSettings((s) => ({ ...s, engine_enabled: v }))}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-nightblue">Procesamiento con IA</div>
            <div className="text-xs text-textgray">Reescritura automática de títulos y contenido usando Claude Sonnet 4.5.</div>
          </div>
          <Switch
            checked={settings.ai_enabled}
            onCheckedChange={(v) => setSettings((s) => ({ ...s, ai_enabled: v }))}
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-textgray">Umbral de similitud para duplicados</Label>
          <Input
            type="number"
            step="0.05"
            min="0"
            max="1"
            value={settings.min_similarity_threshold}
            onChange={(e) => setSettings((s) => ({ ...s, min_similarity_threshold: parseFloat(e.target.value) }))}
            className="mt-2 max-w-[160px]"
          />
          <p className="text-[11px] text-textgray mt-1">Se aplicará cuando el módulo de análisis semántico esté disponible.</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-textgray">Notas</Label>
          <Textarea
            value={settings.notes || ""}
            onChange={(e) => setSettings((s) => ({ ...s, notes: e.target.value }))}
            rows={3}
          />
        </div>
      </div>

      <Button onClick={save} disabled={saving}>{saving ? "Guardando…" : "Guardar configuración"}</Button>
    </section>
  );
}
